import React, { useState, useEffect } from "react";
import { CRMCompany, CRMPerson, CRMProject } from "../types";
import { Building2, Users, User, Briefcase, Plus, Search, Trash2, Edit2, X, ExternalLink, ArrowLeft, Image as ImageIcon, Upload, Loader2, PlusCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { FPT_PROJECT_STATUS_OPTIONS, getProjectStatusTone, normalizeProjectStatus } from "../lib/projectStatus";
import { formatCurrency, cn, getCompanyLogoUrl } from "../utils";
import { toast } from "sonner";
import { GoogleGenAI, Type } from "@google/genai";
import { ConfirmModal } from "./ConfirmModal";

type Tab = "projects" | "companies" | "people" | "domain_users";

const normalizeDomain = (value?: string | null) =>
  (value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");

const extractEmailDomain = (email?: string | null) => {
  const rawDomain = (email || "").split("@")[1] || "";
  return normalizeDomain(rawDomain);
};

const getEffectiveCompanyIdForPerson = (
  person: Pick<CRMPerson, "companyId" | "email">,
  companies: CRMCompany[]
) => {
  if (person.companyId && companies.some(company => company.id === person.companyId)) {
    return person.companyId;
  }

  const emailDomain = extractEmailDomain(person.email);
  if (!emailDomain) return person.companyId || "";

  return companies.find(company => normalizeDomain(company.domain || company.website) === emailDomain)?.id || person.companyId || "";
};

const isInternalDirectoryPerson = (person: Partial<CRMPerson>) =>
  person.directoryType === "internal" ||
  person.personType === "staff" ||
  person.workspaceAccountStatus === "active" ||
  person.workspaceAccountStatus === "inactive" ||
  Boolean(person.hrProfileRef);

interface CRMProps {
  onOpenProject?: (projectId: string) => void;
  onBack: () => void;
}

export function CRM({ onOpenProject, onBack }: CRMProps) {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("projects");
  const [companies, setCompanies] = useState<CRMCompany[]>([]);
  const [people, setPeople] = useState<CRMPerson[]>([]);
  const [bidProjects, setBidProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const backfillCompaniesFromProjects = async (existingCompanies: CRMCompany[], sourceProjects: any[]) => {
    if (!user) return existingCompanies;

    const knownNames = new Set(existingCompanies.map(c => c.name.trim().toLowerCase()));
    const missingCompanies: Array<{ name: string; type: "Production Company" | "Distributor"; companyType: "production" | "distributor" }> = [];

    sourceProjects.forEach(project => {
      const productionName = (project.projectClientName || project.clientName || project.setup?.clientName || "").trim();
      const distributorName = (project.projectDistributorName || project.distributorName || project.setup?.distributorName || "").trim();

      if (productionName && !knownNames.has(productionName.toLowerCase())) {
        knownNames.add(productionName.toLowerCase());
        missingCompanies.push({
          name: productionName,
          type: "Production Company",
          companyType: "production"
        });
      }

      if (distributorName && !knownNames.has(distributorName.toLowerCase())) {
        knownNames.add(distributorName.toLowerCase());
        missingCompanies.push({
          name: distributorName,
          type: "Distributor",
          companyType: "distributor"
        });
      }
    });

    if (missingCompanies.length === 0) return existingCompanies;

    const createdCompanies = await Promise.all(
      missingCompanies.map(async company => {
        const res = await fetch("/api/crm/companies", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            ...company,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })
        });

        if (!res.ok) {
          throw new Error(`Failed to backfill company: ${company.name}`);
        }

        return res.json();
      })
    );

    return [...existingCompanies, ...createdCompanies];
  };

  useEffect(() => {
    const fetchCRMData = async (retries = 3) => {
      if (!user || !profile) {
        setLoading(false);
        return;
      }

      try {
        const [companiesRes, peopleRes, bidProjectsRes] = await Promise.all([
          fetch("/api/crm/companies", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/crm/people", { headers: { "x-user-uid": user.uid } }),
          fetch("/api/projects", { headers: { "x-user-uid": user.uid } })
        ]);

        let companiesData: CRMCompany[] = [];
        let bidProjectsData: any[] = [];

        if (companiesRes.ok) companiesData = await companiesRes.json();
        if (peopleRes.ok) setPeople(await peopleRes.json());
        if (bidProjectsRes.ok) bidProjectsData = await bidProjectsRes.json();

        const hydratedCompanies = await backfillCompaniesFromProjects(companiesData, bidProjectsData);
        setCompanies(hydratedCompanies);
        setBidProjects(bidProjectsData);
        
        setLoading(false);
      } catch (error) {
        if (retries > 0) {
          setTimeout(() => fetchCRMData(retries - 1), 2000);
        } else {
          console.error("Failed to fetch CRM data", error);
          setLoading(false);
        }
      }
    };

    fetchCRMData();
  }, [user, profile]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-12">
      <div className="bg-[#eef5f8] dark:bg-[#162633] px-6 py-4 rounded-lg border border-[#c8d9e5] dark:border-[#2f516a] flex items-center justify-between mb-6 shadow-sm shrink-0">
        <div className="flex flex-col">
          <h2 className="text-2xl font-brandon font-medium text-slate-900 dark:text-slate-100">Relationship Manager</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Manage canonical companies and contacts linked to projects</p>
        </div>
      </div>

      <div className="border-b border-slate-200 dark:border-slate-800 mb-4">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab("projects")}
            className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-[3px] transition-colors ${
              activeTab === "projects"
                ? "border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]"
                : "border-transparent text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"
            }`}
          >
            <Briefcase className="w-[14px] h-[14px]" />
            Projects
          </button>
          <button
            onClick={() => setActiveTab("companies")}
            className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-[3px] transition-colors ${
              activeTab === "companies"
                ? "border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]"
                : "border-transparent text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"
            }`}
          >
            <Building2 className="w-[14px] h-[14px]" />
            Companies
          </button>
          <button
            onClick={() => setActiveTab("people")}
            className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-[3px] transition-colors ${
              activeTab === "people"
                ? "border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]"
                : "border-transparent text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"
            }`}
          >
            <User className="w-[14px] h-[14px]" />
            External Contacts
          </button>
          <button
            onClick={() => setActiveTab("domain_users")}
            className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              activeTab === "domain_users"
                ? "border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]"
                : "border-transparent text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"
            }`}
          >
            <Users className="w-4 h-4" />
            Workspace Staff
          </button>
        </nav>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-4">
        {activeTab === "projects" && (
          <ProjectsTab 
            bidProjects={bidProjects}
            setBidProjects={setBidProjects}
            companies={companies} 
            people={people} 
            setPeople={setPeople}
            onOpenProject={onOpenProject}
          />
        )}
        {activeTab === "companies" && (
          <CompaniesTab 
            companies={companies} 
            setCompanies={setCompanies}
            people={people}
            bidProjects={bidProjects}
          />
        )}
        {activeTab === "people" && (
          <PeopleTab 
            people={people} 
            setPeople={setPeople}
            companies={companies} 
            isDomainTab={false}
          />
        )}
        {activeTab === "domain_users" && (
          <PeopleTab 
            people={people} 
            setPeople={setPeople}
            companies={companies} 
            isDomainTab={true}
          />
        )}
      </div>
    </div>
  );
}

function ProjectsTab({ 
  bidProjects,
  setBidProjects,
  companies, 
  people,
  setPeople,
  onOpenProject
}: { 
  bidProjects: any[],
  setBidProjects: React.Dispatch<React.SetStateAction<any[]>>,
  companies: CRMCompany[], 
  people: CRMPerson[],
  setPeople: React.Dispatch<React.SetStateAction<CRMPerson[]>>,
  onOpenProject?: (projectId: string) => void
}) {
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [collapsedYears, setCollapsedYears] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<Partial<CRMProject>>({ name: "", status: "Bidding", productionCompanyId: "", distributorId: "", personIds: [] });
  const [editingCell, setEditingCell] = useState<{ id: string, field: string } | null>(null);
  const [quickAdd, setQuickAdd] = useState<{ type: 'company' | 'person', projectId?: string, field?: string, callback?: (id: string) => void } | null>(null);
  const [selectedExternalPersonId, setSelectedExternalPersonId] = useState("");
  const [newExternalPersonName, setNewExternalPersonName] = useState("");

  const updateProjectRecord = async (projectId: string, updates: Partial<CRMProject>) => {
    try {
      const productionCompanyName = updates.productionCompanyId
        ? companies.find(c => c.id === updates.productionCompanyId)?.name || ""
        : undefined;
      const distributorName = updates.distributorId
        ? companies.find(c => c.id === updates.distributorId)?.name || ""
        : undefined;

      const data: any = {
        updatedAt: new Date().toISOString(),
        notes: updates.notes,
        personIds: updates.personIds,
        status: updates.status,
        productionCompanyId: updates.productionCompanyId,
        distributorId: updates.distributorId,
        setup: {
          ...(updates.status !== undefined ? { status: updates.status } : {}),
          ...(updates.personIds !== undefined ? { personIds: updates.personIds } : {}),
          ...(updates.productionCompanyId !== undefined ? { productionCompanyId: updates.productionCompanyId || "", clientName: productionCompanyName || "" } : {}),
          ...(updates.distributorId !== undefined ? { distributorId: updates.distributorId || "", distributorName: distributorName || "" } : {}),
        },
      };

      Object.keys(data).forEach((key) => {
        if (data[key] === undefined) delete data[key];
      });
      if (data.setup && Object.keys(data.setup).length === 0) {
        delete data.setup;
      }

      const res = await fetch(`/api/projects/${projectId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        const savedProject = await res.json();
        setBidProjects(prev => prev.map(p => p.id === projectId ? savedProject : p));
      } else {
        throw new Error("Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project");
    }
  };

  const mergedProjects = React.useMemo(() => {
    return bidProjects.map((sourceP: any) => {
      const projectClientName = sourceP.projectClientName || sourceP.clientName || sourceP.setup?.clientName || "";
      const projectDistributorName = sourceP.projectDistributorName || sourceP.distributorName || sourceP.setup?.distributorName || "";
      
      let prodCoId = sourceP.productionCompanyId || sourceP.setup?.productionCompanyId || "";
      if (!prodCoId && projectClientName) {
        const match = companies.find(c => c.name.toLowerCase() === projectClientName.toLowerCase());
        if (match) prodCoId = match.id;
      }
      
      let distId = sourceP.distributorId || sourceP.setup?.distributorId || "";
      if (!distId && projectDistributorName) {
        const match = companies.find(c => c.name.toLowerCase() === projectDistributorName.toLowerCase());
        if (match) distId = match.id;
      }

      return {
        id: sourceP.id,
        name: sourceP.projectName || sourceP.name || "Untitled Project",
        status: sourceP.status || sourceP.setup?.status || "Bidding",
        thumbnail: sourceP.thumbnail || sourceP.setup?.thumbnail || "",
        bidDate: sourceP.bidDate || sourceP.setup?.bidDate || sourceP.createdAt || "",
        productionCompanyId: prodCoId,
        distributorId: distId,
        projectClientName,
        projectDistributorName,
        personIds: sourceP.personIds || sourceP.setup?.personIds || [],
        notes: sourceP.notes || "",
        bidTotal: sourceP.latestTotalCost ?? sourceP.totalCost,
        currency: sourceP.currency || "GBP",
        isLinked: true
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [bidProjects, companies]);

  const groupedProjects = React.useMemo(() => {
    const groups = new Map<string, typeof mergedProjects>();

    mergedProjects.forEach((project) => {
      const year = project.bidDate ? new Date(project.bidDate).getFullYear().toString() : "Unknown";
      const safeYear = year === "NaN" ? "Unknown" : year;
      const bucket = groups.get(safeYear) || [];
      bucket.push(project);
      groups.set(safeYear, bucket);
    });

    return Array.from(groups.entries())
      .map(([year, projectsForYear]) => ({
        year,
        projects: [...projectsForYear].sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => {
        if (a.year === "Unknown") return 1;
        if (b.year === "Unknown") return -1;
        return Number(b.year) - Number(a.year);
      });
  }, [mergedProjects]);

  const selectedProject = mergedProjects.find(project => project.id === selectedProjectId) || null;
  const selectedProjectProductionCompany = selectedProject
    ? companies.find(c => c.id === selectedProject.productionCompanyId)
    : null;
  const externalContacts = React.useMemo(
    () => people.filter(person => person.personType !== "staff" && person.workspaceAccountStatus === "none"),
    [people]
  );
  const selectedProjectPeople = React.useMemo(
    () => (selectedProject?.personIds || []).map(id => people.find(person => person.id === id)).filter(Boolean) as CRMPerson[],
    [selectedProject, people]
  );
  const availableExternalContacts = React.useMemo(
    () => externalContacts.filter(person => !(selectedProject?.personIds || []).includes(person.id)),
    [externalContacts, selectedProject]
  );

  useEffect(() => {
    if (mergedProjects.length === 0) {
      setSelectedProjectId(null);
      return;
    }

    if (!selectedProjectId || !mergedProjects.some(project => project.id === selectedProjectId)) {
      setSelectedProjectId(mergedProjects[0].id);
    }
  }, [mergedProjects, selectedProjectId]);

  useEffect(() => {
    const currentYear = new Date().getFullYear().toString();
    setCollapsedYears(prev => {
      const next = { ...prev };
      groupedProjects.forEach(({ year }) => {
        if (next[year] === undefined) {
          next[year] = year !== currentYear;
        }
      });
      return next;
    });
  }, [groupedProjects]);

  useEffect(() => {
    setSelectedExternalPersonId("");
    setNewExternalPersonName("");
  }, [selectedProjectId]);

  const startEditingProject = (project: CRMProject) => {
    setFormData(project);
    setEditingId(project.id);
    setSelectedProjectId(project.id);
    setIsAdding(true);
  };

  const stopEditingProject = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: "", status: "Bidding", productionCompanyId: "", distributorId: "", personIds: [] });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        notes: formData.notes || "",
        status: formData.status || "Bidding",
        productionCompanyId: formData.productionCompanyId || "",
        distributorId: formData.distributorId || "",
        personIds: formData.personIds || [],
        setup: {
          status: formData.status || "Bidding",
          productionCompanyId: formData.productionCompanyId || "",
          distributorId: formData.distributorId || "",
          personIds: formData.personIds || [],
          clientName: companies.find(c => c.id === formData.productionCompanyId)?.name || "",
          distributorName: companies.find(c => c.id === formData.distributorId)?.name || "",
        },
        updatedAt: new Date().toISOString(),
      };
      
      let res;
      if (editingId) {
        res = await fetch(`/api/projects/${editingId}`, {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify(data)
        });
      } else {
        res = await fetch("/api/projects", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            ...data,
            projectName: formData.name || "Untitled Project",
            createdAt: new Date().toISOString(),
          })
        });
      }

      if (res.ok) {
        const savedProject = await res.json();
        if (editingId) {
          setBidProjects(prev => prev.map(p => p.id === editingId ? savedProject : p));
        } else {
          setBidProjects(prev => [...prev, savedProject]);
        }
        setSelectedProjectId(savedProject.id);
        stopEditingProject();
        toast.success("Project saved successfully");
      } else {
        throw new Error("Failed to save project");
      }
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("Failed to save project");
    }
  };

  const getStatusColor = (status: string) => {
    return getProjectStatusTone(status) || "bg-slate-100 text-slate-600 border border-slate-200";
  };

  const handleProjectPeopleUpdate = async (projectId: string, personIds: string[]) => {
    const uniqueIds = Array.from(new Set(personIds.filter(Boolean)));
    await updateProjectRecord(projectId, { personIds: uniqueIds });
  };

  const handleCreateExternalContactForProject = async () => {
    const trimmedName = newExternalPersonName.trim();
    if (!trimmedName || !selectedProject || !user) return;

    try {
      const res = await fetch("/api/crm/people", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify({
          name: trimmedName,
          companyId: "",
          personType: "external",
          workspaceAccountStatus: "none",
          employmentStatus: "not_applicable",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      });

      if (!res.ok) {
        throw new Error("Failed to create external contact");
      }

      const saved = await res.json();
      setPeople(prev => [...prev, saved]);
      await handleProjectPeopleUpdate(selectedProject.id, [...(selectedProject.personIds || []), saved.id]);
      setNewExternalPersonName("");
      toast.success("External contact created and linked");
    } catch (error) {
      console.error("Error creating external contact:", error);
      toast.error("Failed to create external contact");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Projects</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">View linked project records, statuses, bid totals, and connected companies</p>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_380px]">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
                <tr className="h-10">
                  <th className="px-4 py-0 min-w-[220px] align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Name</th>
                  <th className="px-4 py-0 min-w-[130px] align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                  <th className="px-4 py-3 min-w-[190px]">
                    <span className="hidden sm:inline text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Production Company</span>
                    <span className="sm:hidden text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Prod Co</span>
                  </th>
                  <th className="px-4 py-0 min-w-[170px] align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Distributor</th>
                  <th className="px-4 py-0 min-w-[130px] align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Bid Total</th>
                </tr>
              </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {groupedProjects.map(({ year, projects: projectsForYear }) => (
                  <React.Fragment key={year}>
                    <tr className="bg-slate-100/80 dark:bg-slate-950/80 border-y border-slate-200 dark:border-slate-800">
                      <td colSpan={5} className="px-4 py-2.5">
                        <button
                          type="button"
                          onClick={() => setCollapsedYears(prev => ({ ...prev, [year]: !prev[year] }))}
                          className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
                        >
                          {collapsedYears[year] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          <span>{year}</span>
                          <span className="text-xs font-medium text-slate-400 dark:text-slate-500">{projectsForYear.length} {projectsForYear.length === 1 ? "project" : "projects"}</span>
                        </button>
                      </td>
                    </tr>
                    {!collapsedYears[year] && projectsForYear.map(p => (
                      <tr
                        key={p.id}
                        className={cn("hover:bg-slate-50 dark:hover:bg-slate-800/60", selectedProjectId === p.id && "bg-indigo-50/70 dark:bg-indigo-950/40")}
                        onClick={() => {
                          setSelectedProjectId(p.id);
                          if (!isAdding) setEditingId(null);
                        }}
                      >
                <td className="px-4 py-3">
                  <div className="max-w-[240px] truncate font-medium text-slate-900 dark:text-slate-100">
                    {p.name}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span 
                    className={cn(
                      "inline-flex min-w-[112px] items-center justify-center whitespace-nowrap px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider",
                      getStatusColor(p.status)
                    )}
                  >
                    {normalizeProjectStatus(p.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                  <div className="flex max-w-[220px] items-center gap-1 truncate">
                    {companies.find(c => c.id === p.productionCompanyId)?.name || (
                      p.projectClientName ? (
                        <span className="text-slate-400 dark:text-slate-500 italic">{p.projectClientName}</span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">-</span>
                      )
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                  <div className="flex max-w-[190px] items-center gap-1 truncate">
                    {companies.find(c => c.id === p.distributorId)?.name || (
                      p.projectDistributorName ? (
                        <span className="text-slate-400 dark:text-slate-500 italic">{p.projectDistributorName}</span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">-</span>
                      )
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-medium text-indigo-600 whitespace-nowrap">
                  {p.bidTotal !== undefined ? formatCurrency(p.bidTotal, p.currency) : "-"}
                </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {mergedProjects.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">No projects found.</td></tr>
                )}
          </tbody>
            </table>
          </div>
        </div>

        <aside className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 shadow-sm">
          {isAdding ? (
            <form onSubmit={handleSubmit} className="space-y-0">
              <div className="flex h-10 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-0">
                <div>
                  <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {editingId ? "Edit Project" : "Add Project"}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={stopEditingProject}
                  className="rounded-lg p-2 text-slate-400 dark:text-slate-500 hover:bg-white dark:hover:bg-slate-900 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-4">

              <div className="space-y-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Project Name</label>
                  <input type="text" readOnly value={formData.name || ""} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 outline-none cursor-not-allowed dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400" />
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">Project name is managed in the dashboard.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Status</label>
                  <select 
                    value={formData.status || ""} 
                    onChange={e => setFormData({...formData, status: e.target.value})} 
                    className={cn(
                      "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition-colors focus:ring-2 focus:ring-indigo-500",
                      getProjectStatusTone(formData.status)
                    )}
                  >
                    {FPT_PROJECT_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Production Company</label>
                  <select 
                    value={formData.productionCompanyId} 
                    onChange={e => {
                      if (e.target.value === 'ADD_NEW') {
                        setQuickAdd({ 
                          type: 'company', 
                          callback: (id) => setFormData(prev => ({ ...prev, productionCompanyId: id }))
                        });
                      } else {
                        setFormData({...formData, productionCompanyId: e.target.value});
                      }
                    }} 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  >
                    <option value="">Select Company...</option>
                    <option value="ADD_NEW" className="font-bold text-indigo-600">+ Add new Production Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Distributor</label>
                  <select 
                    value={formData.distributorId} 
                    onChange={e => {
                      if (e.target.value === 'ADD_NEW') {
                        setQuickAdd({ 
                          type: 'company', 
                          callback: (id) => setFormData(prev => ({ ...prev, distributorId: id }))
                        });
                      } else {
                        setFormData({...formData, distributorId: e.target.value});
                      }
                    }} 
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  >
                    <option value="">Select Distributor...</option>
                    <option value="ADD_NEW" className="font-bold text-indigo-600">+ Add new Distributor</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Key People</label>
                  <div className="flex flex-col gap-2">
                    <select 
                      multiple 
                      value={formData.personIds} 
                      onChange={e => setFormData({...formData, personIds: Array.from(e.target.selectedOptions, option => option.value)})} 
                      className="h-24 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    >
                      {externalContacts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button 
                      type="button"
                      onClick={() => setQuickAdd({ 
                        type: 'person', 
                        callback: (id) => setFormData(prev => ({ ...prev, personIds: [...(prev.personIds || []), id] }))
                      })}
                      className="flex items-center gap-1 text-xs text-indigo-600 font-medium hover:text-indigo-700 w-fit"
                    >
                      <Plus className="w-3 h-3" /> Add new Person
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Hold Ctrl/Cmd to select multiple</p>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Notes</label>
                  <textarea value={formData.notes || ""} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" rows={5} />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={stopEditingProject} className="px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-xs font-medium">Cancel</button>
                <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium">Save</button>
              </div>
              </div>
            </form>
          ) : selectedProject ? (
            <div>
              <div className="flex h-10 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-0">
                <div>
                  <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Project Details</h4>
                </div>
              </div>
              <div className="p-5">
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900 px-4 py-4 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                    {selectedProject.thumbnail ? (
                      <img
                        src={selectedProject.thumbnail}
                        alt={selectedProject.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="h-4 w-4 text-slate-300 dark:text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xl font-brandon font-medium leading-tight text-slate-900 dark:text-slate-100">
                      {selectedProject.name}
                    </h4>
                    <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                      <div className="flex min-w-0 items-center gap-2 truncate">
                        {selectedProjectProductionCompany && getCompanyLogoUrl(selectedProjectProductionCompany) && (
                          <img
                            src={getCompanyLogoUrl(selectedProjectProductionCompany)!}
                            alt={selectedProjectProductionCompany.name}
                            className="h-4 w-4 shrink-0 object-contain"
                          />
                        )}
                        <span className="truncate">
                          {selectedProjectProductionCompany?.name || selectedProject.projectClientName || "No Production Company"}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider">Total Bid Estimate</span>
                        <span className="font-bold text-indigo-600">
                          {selectedProject.bidTotal !== undefined ? formatCurrency(selectedProject.bidTotal, selectedProject.currency) : "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className={cn("inline-flex px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider", getStatusColor(selectedProject.status))}>
                    {normalizeProjectStatus(selectedProject.status)}
                  </span>
                  <button
                    onClick={() => startEditingProject(selectedProject)}
                    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Edit
                  </button>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="px-1 py-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Production Company</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    {companies.find(c => c.id === selectedProject.productionCompanyId)?.name || selectedProject.projectClientName || "-"}
                  </p>
                </div>
                <div className="px-1 py-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Distributor</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    {companies.find(c => c.id === selectedProject.distributorId)?.name || selectedProject.projectDistributorName || "-"}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Key People</p>
                  <div className="mt-2 space-y-3">
                    {selectedProjectPeople.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedProjectPeople.map(person => (
                          <button
                            key={person.id}
                            type="button"
                            onClick={() => handleProjectPeopleUpdate(selectedProject.id, (selectedProject.personIds || []).filter(id => id !== person.id))}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200"
                            title={`Remove ${person.name}`}
                          >
                            <span>{person.name}</span>
                            <X className="h-3 w-3" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-700 dark:text-slate-200">No linked people yet.</p>
                    )}

                    <div className="flex items-center gap-2">
                      <select
                        value={selectedExternalPersonId}
                        onChange={(e) => setSelectedExternalPersonId(e.target.value)}
                        className="flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-700 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Add external contact...</option>
                        {availableExternalContacts.map(person => (
                          <option key={person.id} value={person.id}>{person.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={!selectedExternalPersonId}
                        onClick={() => {
                          if (!selectedExternalPersonId) return;
                          handleProjectPeopleUpdate(selectedProject.id, [...(selectedProject.personIds || []), selectedExternalPersonId]);
                          setSelectedExternalPersonId("");
                        }}
                        className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>

                    <input
                      type="text"
                      value={newExternalPersonName}
                      onChange={(e) => setNewExternalPersonName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateExternalContactForProject();
                        }
                      }}
                      placeholder="Type a new external contact and press Enter"
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-700 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Notes</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                    {selectedProject.notes || "No notes yet."}
                  </p>
                </div>
              </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-6 text-center">
              <Briefcase className="h-10 w-10 text-slate-300 dark:text-slate-600" />
              <h4 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">Select a project</h4>
              <p className="mt-2 max-w-xs text-sm text-slate-500 dark:text-slate-400">
                Choose a project from the list to see linked company and people details here.
              </p>
            </div>
          )}
        </aside>
      </div>
      {quickAdd && (
        <QuickAddModal 
          type={quickAdd.type} 
          companies={companies}
          onClose={() => setQuickAdd(null)} 
          onSuccess={async (id) => {
            if (quickAdd.callback) {
              quickAdd.callback(id);
            } else if (quickAdd.projectId && quickAdd.field) {
              if (quickAdd.field === 'personIds') {
                const project = mergedProjects.find(p => p.id === quickAdd.projectId);
                const currentPeople = project?.personIds || [];
                await updateProjectRecord(quickAdd.projectId, { personIds: [...currentPeople, id] });
              } else {
                await updateProjectRecord(quickAdd.projectId, { [quickAdd.field]: id } as Partial<CRMProject>);
              }
            }
            setQuickAdd(null);
          }} 
        />
      )}
    </div>
  );
}

function QuickAddModal({ 
  type, 
  onClose, 
  onSuccess,
  companies 
}: { 
  type: 'company' | 'person', 
  onClose: () => void, 
  onSuccess: (id: string) => void,
  companies: CRMCompany[]
}) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res;
      if (type === 'company') {
        res = await fetch("/api/crm/companies", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            name,
            companyType: "production",
            type: "Production Company",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        });
      } else {
        res = await fetch("/api/crm/people", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            name,
            companyId,
            personType: "external",
            workspaceAccountStatus: "none",
            employmentStatus: "not_applicable",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        });
      }

      if (res.ok) {
        const data = await res.json();
        onSuccess(data.id);
      } else {
        throw new Error("Failed to add CRM entry");
      }
    } catch (error) {
      console.error("Error adding CRM entry:", error);
      toast.error("Failed to add CRM entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Add New {type === 'company' ? 'Company' : 'Person'}</h3>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Name</label>
            <input 
              autoFocus
              type="text" 
              required 
              value={name} 
              onChange={e => setName(e.target.value)} 
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" 
              placeholder={`Enter ${type} name...`}
            />
          </div>
          {type === 'person' && (
            <div>
              <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Company (Optional)</label>
              <select 
                value={companyId} 
                onChange={e => setCompanyId(e.target.value)} 
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100"
              >
                <option value="">Select Company...</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-xs font-medium">Cancel</button>
            <button 
              type="submit" 
              disabled={loading || !name}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompaniesTab({ 
  companies, 
  setCompanies,
  people,
  bidProjects
}: { 
  companies: CRMCompany[], 
  setCompanies: React.Dispatch<React.SetStateAction<CRMCompany[]>>,
  people: CRMPerson[],
  bidProjects: any[]
}) {
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CRMCompany>>({ name: "", type: "Production Company", companyType: "production", website: "", domain: "", notes: "", logo: "" });
  const [researchingNotesId, setResearchingNotesId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const selectedCompany = companies.find(c => c.id === selectedCompanyId) || null;
  const sortedCompanies = React.useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies]
  );
  const associatedPeople = React.useMemo(() => {
    if (!selectedCompany) return [];
    return people
      .filter(person => getEffectiveCompanyIdForPerson(person, companies) === selectedCompany.id)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCompany, people, companies]);
  const associatedProjects = React.useMemo(() => {
    if (!selectedCompany) return [];

    const companyName = selectedCompany.name.trim().toLowerCase();

    return bidProjects
      .filter(project => {
        const legacyProject = project as CRMProject & {
          projectName?: string;
          clientName?: string;
          distributorName?: string;
          projectClientName?: string;
          projectDistributorName?: string;
          setup?: {
            clientName?: string;
            distributorName?: string;
            thumbnail?: string;
          };
          thumbnail?: string;
        };
        const matchesById =
          project.productionCompanyId === selectedCompany.id ||
          project.distributorId === selectedCompany.id;

        if (matchesById) return true;

        const productionName = (
          legacyProject.projectClientName ||
          legacyProject.clientName ||
          legacyProject.setup?.clientName ||
          ""
        ).trim().toLowerCase();
        const distributorName = (
          legacyProject.projectDistributorName ||
          legacyProject.distributorName ||
          legacyProject.setup?.distributorName ||
          ""
        ).trim().toLowerCase();

        return productionName === companyName || distributorName === companyName;
      })
      .sort((a, b) => (a.name || (a as any).projectName || "").localeCompare(b.name || (b as any).projectName || ""));
  }, [selectedCompany, bidProjects]);
  const getCompanyTypeLabel = (company: Partial<CRMCompany> | null | undefined) =>
    company?.type || company?.companyType || "-";

  useEffect(() => {
    if (sortedCompanies.length === 0) {
      setSelectedCompanyId(null);
      return;
    }

    if (!selectedCompanyId || !sortedCompanies.some(c => c.id === selectedCompanyId)) {
      setSelectedCompanyId(sortedCompanies[0].id);
    }
  }, [sortedCompanies, selectedCompanyId]);

  const startCreatingCompany = () => {
    setIsAdding(true);
    setEditingId(null);
    setSelectedCompanyId(null);
    setFormData({
      name: "",
      type: "Production Company",
      companyType: "production",
      website: "",
      domain: "",
      notes: "",
      logo: ""
    });
  };

  const startEditingCompany = (company: CRMCompany) => {
    setFormData(company);
    setEditingId(company.id);
    setSelectedCompanyId(company.id);
    setIsAdding(true);
  };

  const stopEditingCompany = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: "", type: "Production Company", companyType: "production", website: "", domain: "", notes: "", logo: "" });
  };

  const handleResearchCompanyNotes = async (company: Pick<CRMCompany, "name" | "website" | "domain">, companyId?: string) => {
    if (!process.env.GEMINI_API_KEY) {
      toast.error("Gemini API Key is missing.");
      return;
    }

    const researchKey = companyId || "new";
    setResearchingNotesId(researchKey);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      const websiteHint = company.website?.trim()
        ? `Use ${company.website.trim()} as the primary source when possible, then use broader web search only to fill gaps or recent credits.`
        : company.domain?.trim()
          ? `Prefer sources from the ${company.domain.trim()} domain first, then use broader web search to fill gaps or recent credits.`
          : "Use broader web search because no website is provided.";

      const prompt = [
        `Research the company "${company.name}".`,
        websiteHint,
        "Return exactly two concise sentences describing what the company does and where it sits in the market.",
        "Then return up to five recent or well-known previous projects, preferring recent credits over old ones.",
        "Avoid speculation and avoid long marketing copy.",
      ].join(" ");

      const response = await ai.models.generateContent({
        model: "gemini-3.1-pro-preview",
        contents: prompt,
        config: {
          tools: [{ googleSearch: {} }],
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              summary: {
                type: Type.STRING,
                description: "Exactly two concise sentences about the company."
              },
              recentProjects: {
                type: Type.ARRAY,
                items: { type: Type.STRING },
                description: "Up to five recent or notable projects."
              }
            },
            required: ["summary", "recentProjects"]
          }
        }
      });

      const responseText = response.text?.trim();
      if (!responseText) {
        throw new Error("No response returned");
      }

      const data = JSON.parse(responseText);
      const recentProjects = Array.isArray(data.recentProjects)
        ? data.recentProjects.map((item: unknown) => String(item).trim()).filter(Boolean).slice(0, 5)
        : [];
      const notes = [
        String(data.summary || "").trim(),
        recentProjects.length > 0
          ? `Recent projects:\n${recentProjects.map(project => `• ${project}`).join("\n")}`
          : ""
      ].filter(Boolean).join("\n\n");

      if (!notes) {
        throw new Error("No usable company notes returned");
      }

      if (companyId) {
        const res = await fetch(`/api/crm/companies/${companyId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({ notes })
        });

        if (!res.ok) {
          throw new Error("Failed to update company notes");
        }

        const updated = await res.json();
        setCompanies(prev => prev.map(c => c.id === companyId ? updated : c));
        if (editingId === companyId) {
          setFormData(prev => ({ ...prev, notes }));
        }
      } else {
        setFormData(prev => ({ ...prev, notes }));
      }

      toast.success("Company notes generated");
    } catch (error) {
      console.error("Error researching company notes:", error);
      toast.error("Failed to generate company notes");
    } finally {
      setResearchingNotesId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };
      
      let res;
      if (editingId) {
        res = await fetch(`/api/crm/companies/${editingId}`, {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify(data)
        });
      } else {
        res = await fetch("/api/crm/companies", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            ...data,
            createdAt: new Date().toISOString(),
          })
        });
      }

      if (res.ok) {
        const saved = await res.json();
        if (editingId) {
          setCompanies(prev => prev.map(c => c.id === editingId ? saved : c));
        } else {
          setCompanies(prev => [...prev, saved]);
        }
        setSelectedCompanyId(saved.id);
        stopEditingCompany();
        toast.success("Company saved successfully");
      } else {
        throw new Error("Failed to save company");
      }
    } catch (error) {
      console.error("Error saving company:", error);
      toast.error("Failed to save company");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await fetch(`/api/crm/companies/${deleteId}`, {
        method: "DELETE",
        headers: {
          "x-user-uid": user.uid
        }
      });
      if (res.ok) {
        setCompanies(prev => prev.filter(c => c.id !== deleteId));
        if (selectedCompanyId === deleteId) {
          const remaining = companies.filter(c => c.id !== deleteId);
          setSelectedCompanyId(remaining[0]?.id || null);
        }
        if (editingId === deleteId) {
          stopEditingCompany();
        }
        setDeleteId(null);
        toast.success("Company deleted successfully");
      } else {
        throw new Error("Failed to delete company");
      }
    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error("Failed to delete company");
    }
  };

  return (
    <div>
      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Company"
        message="Are you sure you want to delete this company? This action cannot be undone."
        confirmText="Delete"
        isDestructive
      />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Companies</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Manage company records, domains, websites, and linked contacts</p>
        </div>
        <button onClick={startCreatingCompany} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium">
          <Plus className="w-4 h-4" /> Add Company
        </button>
      </div>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_380px]">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
                <tr className="h-10">
                  <th className="w-16 px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Icon</th>
                  <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Name</th>
                  <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Type</th>
                  <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Website</th>
                  <th className="px-4 py-0 text-right align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {sortedCompanies.map(c => (
                  <tr
                    key={c.id}
                    className={cn(
                      "group cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60",
                      selectedCompanyId === c.id && "bg-indigo-50/70 dark:bg-indigo-950/40"
                    )}
                    onClick={() => {
                      setSelectedCompanyId(c.id);
                      if (!isAdding) setEditingId(null);
                    }}
                  >
                    <td className="px-4 py-2">
                      <div className="h-10 w-10 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden border border-slate-200 dark:border-slate-700">
                        {getCompanyLogoUrl(c) ? (
                          <img src={getCompanyLogoUrl(c)!} alt={c.name} className="w-full h-full object-contain p-1" />
                        ) : (
                          <Building2 className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-900 dark:text-slate-100">{c.name}</td>
                    <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{c.type || c.companyType || "-"}</td>
                    <td className="px-4 py-2">
                      {c.website && (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="noreferrer"
                          onClick={event => event.stopPropagation()}
                          className="text-slate-600 visited:text-slate-600 dark:text-slate-300 dark:visited:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 break-all font-medium"
                        >
                          {c.website}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={(event) => { event.stopPropagation(); startEditingCompany(c); }} className="p-1 text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-300"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={(event) => { event.stopPropagation(); setDeleteId(c.id); }} className="p-1 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 ml-2"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {sortedCompanies.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">No companies found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          {isAdding ? (
            <form onSubmit={handleSubmit} className="space-y-0">
              <div className="flex min-h-[61px] items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-2.5">
                <div>
                  <h4 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    {editingId ? formData.name || "Edit company" : "Add company"}
                  </h4>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {editingId ? "Update the selected company record and linked metadata" : "Create a new canonical company record for relationships and projects"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={stopEditingCompany}
                  className="rounded-lg p-2 text-slate-400 dark:text-slate-500 hover:bg-white dark:hover:bg-slate-900 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-4">

              <div className="flex items-center gap-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="w-20 h-20 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                  {formData.logo ? (
                    <img src={formData.logo} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-8 h-8 text-slate-300 dark:text-slate-500" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Company Logo</label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Logo display follows the company website favicon and remains visible while editing.
                  </p>
                </div>
              </div>

              <div className="space-y-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Company Name</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Type</label>
                  <select value={formData.type} onChange={e => setFormData({
                    ...formData,
                    type: e.target.value,
                    companyType:
                      e.target.value === "Distributor" ? "distributor" :
                      e.target.value === "Production Company" ? "production" :
                      e.target.value === "Agency" ? "client" :
                      e.target.value === "VFX Studio" ? "vendor" :
                      "vendor"
                  })} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100">
                    <option value="Production Company">Production Company</option>
                    <option value="Distributor">Distributor</option>
                    <option value="VFX Studio">VFX Studio</option>
                    <option value="Agency">Agency</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Website</label>
                  <input type="url" value={formData.website || ""} onChange={e => setFormData({...formData, website: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Email Domain</label>
                  <input type="text" placeholder="company.com" value={formData.domain || ""} onChange={e => setFormData({...formData, domain: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Notes</label>
                  <div className="mb-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleResearchCompanyNotes({
                        name: formData.name || "",
                        website: formData.website || "",
                        domain: formData.domain || ""
                      }, editingId || undefined)}
                      disabled={!formData.name || researchingNotesId === (editingId || "new")}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                    >
                      {researchingNotesId === (editingId || "new") ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Search className="h-3.5 w-3.5" />
                      )}
                      AI Complete Notes
                    </button>
                  </div>
                  <textarea value={formData.notes || ""} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" rows={5} />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={stopEditingCompany} className="px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-xs font-medium">Cancel</button>
                <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium">Save</button>
              </div>
              </div>
            </form>
          ) : selectedCompany ? (
            <div>
              <div className="flex h-10 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-0">
                <div>
                  <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Company Details</h4>
                </div>
              </div>
              <div className="p-5">
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-4 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950">
                      {getCompanyLogoUrl(selectedCompany) ? (
                        <img src={getCompanyLogoUrl(selectedCompany)!} alt={selectedCompany.name} className="h-full w-full object-contain p-2" />
                      ) : (
                        <Building2 className="h-5 w-5 text-slate-300 dark:text-slate-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xl font-brandon font-medium leading-tight text-slate-900 dark:text-slate-100">
                        {selectedCompany.name}
                      </h4>
                      <div className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
                        <span>{getCompanyTypeLabel(selectedCompany)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                      {getCompanyTypeLabel(selectedCompany)}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => startEditingCompany(selectedCompany)} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Edit</button>
                      <button onClick={() => setDeleteId(selectedCompany.id)} className="rounded-lg border border-red-200 dark:border-red-900/40 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30">Delete</button>
                    </div>
                  </div>
                </div>

              <div className="mt-5 space-y-3">
                <div className="px-1 py-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Website</p>
                  {selectedCompany.website ? (
                    <a
                      href={selectedCompany.website}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-start gap-1.5 text-sm font-medium text-slate-700 visited:text-slate-700 dark:text-slate-200 dark:visited:text-slate-200 hover:text-slate-900 dark:hover:text-slate-100 break-all"
                    >
                      <span className="break-all">{selectedCompany.website}</span>
                      <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    </a>
                  ) : (
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">-</p>
                  )}
                </div>
                <div className="px-1 py-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Email Domain</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{selectedCompany.domain || "-"}</p>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Projects</p>
                  <div className="mt-2 space-y-2">
                    {associatedProjects.length > 0 ? (
                      associatedProjects.map(project => {
                        const projectWithThumbnail = project as CRMProject & {
                          projectName?: string;
                          totalCost?: number;
                          latestTotalCost?: number;
                          currency?: string;
                          thumbnail?: string;
                          setup?: { thumbnail?: string };
                        };
                        const displayProjectName = project.name || projectWithThumbnail.projectName || "Untitled Project";
                        const displayThumbnail = projectWithThumbnail.thumbnail || projectWithThumbnail.setup?.thumbnail || "";
                        const displayBidTotal =
                          projectWithThumbnail.latestTotalCost ??
                          projectWithThumbnail.totalCost ??
                          (project as any).bidTotal;
                        const displayCurrency = projectWithThumbnail.currency || (project as any).currency || "GBP";
                        return (
                        <div key={project.id} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                            {displayThumbnail ? (
                              <img
                                src={displayThumbnail}
                                alt={displayProjectName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <ImageIcon className="h-3.5 w-3.5 text-slate-300 dark:text-slate-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm text-slate-700 dark:text-slate-200">
                              {displayProjectName}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              Total Bid Estimate{" "}
                              <span className="font-medium text-[#ffcc01]">
                                {displayBidTotal !== undefined ? formatCurrency(displayBidTotal, displayCurrency) : "-"}
                              </span>
                            </p>
                          </div>
                        </div>
                      )})
                    ) : (
                      <p className="text-sm text-slate-700 dark:text-slate-200">No linked projects yet.</p>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">People</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    {associatedPeople.length > 0
                      ? associatedPeople.map(person => person.name).join(", ")
                      : "No linked people yet."}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Notes</p>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleResearchCompanyNotes(selectedCompany, selectedCompany.id)}
                      disabled={researchingNotesId === selectedCompany.id}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                    >
                      {researchingNotesId === selectedCompany.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Search className="h-3.5 w-3.5" />
                      )}
                      Refresh Notes
                    </button>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                    {selectedCompany.notes || "No notes yet."}
                  </p>
                </div>
              </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-6 text-center">
              <Building2 className="h-10 w-10 text-slate-300 dark:text-slate-600" />
              <h4 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">Select a company</h4>
              <p className="mt-2 max-w-xs text-sm text-slate-500 dark:text-slate-400">
                Choose a company from the list to see its details here, or add a new one.
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function PeopleTab({ 
  people, 
  setPeople,
  companies,
  isDomainTab 
}: { 
  people: CRMPerson[], 
  setPeople: React.Dispatch<React.SetStateAction<CRMPerson[]>>,
  companies: CRMCompany[],
  isDomainTab?: boolean
}) {
  const { user, isAdmin } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CRMPerson>>({
    name: "",
    email: "",
    phone: "",
    role: "",
    roleTitle: "",
    department: "",
    companyId: "",
    personType: isDomainTab ? "staff" : "external",
    workspaceAccountStatus: isDomainTab ? "active" : "none",
    employmentStatus: isDomainTab ? "active" : "not_applicable",
    notes: "",
    photoUrl: ""
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [searchingId, setSearchingId] = useState<string | null>(null);
  const [collapsedDepts, setCollapsedDepts] = useState<Record<string, boolean>>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const findCompanyIdByEmail = (email?: string | null) => {
    const emailDomain = extractEmailDomain(email);
    if (!emailDomain) return "";
    return companies.find(company => normalizeDomain(company.domain || company.website) === emailDomain)?.id || "";
  };

  const displayPeople = people.filter(p => {
    const isInternalPerson = isInternalDirectoryPerson(p);
    return isDomainTab ? isInternalPerson : !isInternalPerson;
  });

  const groupedDomainUsers = React.useMemo(() => {
    if (!isDomainTab) return {};
    return displayPeople.reduce((acc, p) => {
      const dept = p.department || "Unassigned";
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(p);
      return acc;
    }, {} as Record<string, CRMPerson[]>);
  }, [displayPeople, isDomainTab]);

  const getPersonCompanyId = (person: CRMPerson) => getEffectiveCompanyIdForPerson(person, companies);
  const getPersonCompanyName = (person: CRMPerson) =>
    companies.find(company => company.id === getPersonCompanyId(person))?.name || "-";
  const selectedPerson = displayPeople.find(person => person.id === selectedPersonId) || null;

  useEffect(() => {
    if (displayPeople.length === 0) {
      setSelectedPersonId(null);
      return;
    }

    if (!selectedPersonId || !displayPeople.some(person => person.id === selectedPersonId)) {
      setSelectedPersonId(displayPeople[0].id);
    }
  }, [displayPeople, selectedPersonId]);

  const resetFormData = (): Partial<CRMPerson> => ({
    name: "",
    email: "",
    phone: "",
    role: "",
    roleTitle: "",
    department: "",
    companyId: "",
    personType: isDomainTab ? "staff" : "external",
    workspaceAccountStatus: isDomainTab ? "active" : "none",
    employmentStatus: isDomainTab ? "active" : "not_applicable",
    notes: "",
    photoUrl: ""
  });

  const startCreatingPerson = () => {
    setIsAdding(true);
    setEditingId(null);
    setSelectedPersonId(null);
    setFormData(resetFormData());
  };

  const startEditingPerson = (person: CRMPerson) => {
    setIsAdding(true);
    setEditingId(person.id);
    setSelectedPersonId(person.id);
    setFormData({ ...person, companyId: getPersonCompanyId(person) });
  };

  const stopEditingPerson = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(resetFormData());
  };

  const handleSyncWorkspace = async () => {
    if (!user) return;
    try {
      setIsSyncing(true);
      const res = await fetch("/api/crm/workspace/sync", {
        method: "POST",
        headers: { "x-user-uid": user.uid }
      });
      
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await res.json();
        if (res.ok) {
          toast.success(`Synced ${data.added} new staff and updated ${data.updated} existing staff from Workspace.`);
          // Refresh people list
          const peopleRes = await fetch("/api/crm/people", { headers: { "x-user-uid": user.uid } });
          if (peopleRes.ok) {
            setPeople(await peopleRes.json());
          }
        } else {
          toast.error(data.error || "Failed to sync workspace users");
        }
      } else {
        // Handle HTML or other non-JSON responses (e.g. 502 Bad Gateway, 403 Forbidden intercepted by proxy)
        if (res.status === 403) {
          toast.error("Permission denied. You must be a Google Workspace Admin to sync users, and the app needs the Admin Directory scope.");
        } else {
          toast.error(`Server error: ${res.status} ${res.statusText}`);
        }
      }
    } catch (error: any) {
      console.error("Error syncing workspace users:", error);
      toast.error(error.message || "Failed to sync workspace users");
    } finally {
      setIsSyncing(false);
    }
  };

  const resizeImage = (img: HTMLImageElement): string => {
    const canvas = document.createElement("canvas");
    canvas.width = 500;
    canvas.height = 500;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    const size = Math.min(img.width, img.height);
    const x = (img.width - size) / 2;
    const y = (img.height - size) / 2;

    ctx.drawImage(img, x, y, size, size, 0, 0, 500, 500);
    return canvas.toDataURL("image/jpeg", 0.8);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>, personId?: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const dataUrl = resizeImage(img);
        if (personId) {
          try {
            const res = await fetch(`/api/crm/people/${personId}`, {
              method: "PUT",
              headers: { 
                "Content-Type": "application/json",
                "x-user-uid": user.uid
              },
              body: JSON.stringify({ photoUrl: dataUrl })
            });
            if (!res.ok) throw new Error("Failed to update photo");
          } catch (error) {
            console.error("Error updating photo:", error);
            toast.error("Failed to update photo");
          }
        } else {
          setFormData(prev => ({ ...prev, photoUrl: dataUrl }));
        }
        setUploadingId(null);
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleSearchPhoto = async (personName: string, personId?: string) => {
    if (!process.env.GEMINI_API_KEY) {
      toast.error("Gemini API Key is missing.");
      return;
    }

    if (personId) setSearchingId(personId);
    else setSearchingId("new");

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      const prompt = `Find a high quality square professional headshot or profile photo URL for '${personName}'. Prefer square icons or photos with a clean background.`;

      const response = await ai.models.generateContent({
        model: "gemini-3.1-pro-preview",
        contents: prompt,
        config: {
          tools: [{ googleSearch: {} }],
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              photoUrl: {
                type: Type.STRING,
                description: "A direct URL to a JPEG or PNG image of the person.",
              }
            },
            required: ["photoUrl"]
          }
        },
      });

      const responseText = response.text?.trim();
      if (responseText) {
        const data = JSON.parse(responseText);
        if (data.photoUrl && data.photoUrl.startsWith("http")) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = async () => {
            const dataUrl = resizeImage(img);
            if (personId) {
              try {
                const res = await fetch(`/api/crm/people/${personId}`, {
                  method: "PUT",
                  headers: { 
                    "Content-Type": "application/json",
                    "x-user-uid": user.uid
                  },
                  body: JSON.stringify({ photoUrl: dataUrl })
                });
                if (res.ok) {
                  const updated = await res.json();
            setPeople(prev => prev.map(p => p.id === personId ? updated : p));
                } else {
                  throw new Error("Failed to update photo");
                }
              } catch (error) {
                console.error("Error updating photo:", error);
                toast.error("Failed to update photo");
              }
            } else {
              setFormData(prev => ({ ...prev, photoUrl: dataUrl }));
            }
          };
          img.onerror = () => {
            toast.error("Failed to load the image from the found URL.");
          };
          img.src = data.photoUrl;
        } else {
          toast.error("Could not find a suitable photo URL.");
        }
      }
    } catch (err) {
      console.error("Error searching for photo:", err);
      toast.error("Failed to search for photo.");
    } finally {
      setSearchingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let data = {
        ...formData,
        roleTitle: formData.roleTitle || formData.role || "",
        role: formData.role || formData.roleTitle || "",
        personType: isDomainTab ? "staff" : "external",
        workspaceAccountStatus: isDomainTab ? (formData.workspaceAccountStatus || "active") : "none",
        employmentStatus: isDomainTab ? (formData.employmentStatus || "active") : "not_applicable",
        updatedAt: new Date().toISOString(),
      };

      // Smart Domain Matching
      if (data.email && !data.companyId) {
        data.companyId = findCompanyIdByEmail(data.email);
      }

      let res;
      if (editingId) {
        res = await fetch(`/api/crm/people/${editingId}`, {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify(data)
        });
      } else {
        res = await fetch("/api/crm/people", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-user-uid": user.uid
          },
          body: JSON.stringify({
            ...data,
            createdAt: new Date().toISOString(),
          })
        });
      }

      if (res.ok) {
        const saved = await res.json();
        if (editingId) {
          setPeople(prev => prev.map(p => p.id === editingId ? saved : p));
        } else {
          setPeople(prev => [...prev, saved]);
        }
        setSelectedPersonId(saved.id);
        stopEditingPerson();
        toast.success("Person saved successfully");
      } else {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to save person");
      }
    } catch (error: any) {
      console.error("Error saving person:", error);
      toast.error(error.message || "Failed to save person");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await fetch(`/api/crm/people/${deleteId}`, {
        method: "DELETE",
        headers: {
          "x-user-uid": user.uid
        }
      });
      if (res.ok) {
        setPeople(prev => prev.filter(p => p.id !== deleteId));
        if (selectedPersonId === deleteId) {
          const remaining = displayPeople.filter(p => p.id !== deleteId);
          setSelectedPersonId(remaining[0]?.id || null);
        }
        if (editingId === deleteId) {
          stopEditingPerson();
        }
        setDeleteId(null);
        toast.success("Person deleted successfully");
      } else {
        throw new Error("Failed to delete person");
      }
    } catch (error) {
      console.error("Error deleting person:", error);
      toast.error("Failed to delete person");
    }
  };

  const renderPersonRow = (p: CRMPerson) => (
    <tr
      key={p.id}
      className={cn(
        "group cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60",
        selectedPersonId === p.id && "bg-indigo-50/70 dark:bg-indigo-950/40"
      )}
      onClick={() => {
        setSelectedPersonId(p.id);
        if (!isAdding) setEditingId(null);
      }}
    >
      <td className="px-4 py-3">
        <div className="relative w-[50px] h-[50px] bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden border border-slate-200 dark:border-slate-700">
          <User className="w-5 h-5 text-slate-400 dark:text-slate-500" />
          {p.photoUrl ? (
            <img
              src={p.photoUrl}
              alt={p.name}
              className="absolute inset-0 h-full w-full object-cover"
              referrerPolicy="no-referrer"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{p.name}</td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.roleTitle || p.role || p.title || "-"}</td>
      {isDomainTab && <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.department || "-"}</td>}
      {!isDomainTab && <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{getPersonCompanyName(p)}</td>}
      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
        {p.email && <div><a href={`mailto:${p.email}`} className="text-indigo-600 dark:text-indigo-300 hover:underline">{p.email}</a></div>}
        {p.phone && <div>{p.phone}</div>}
      </td>
      <td className="px-4 py-3 text-right">
        {(!isDomainTab || isAdmin) && (
          <>
            <button
              onClick={(event) => {
                event.stopPropagation();
                startEditingPerson(p);
              }}
              className="p-1 text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-300"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={(event) => { event.stopPropagation(); setDeleteId(p.id); }} className="p-1 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 ml-2"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </td>
    </tr>
  );

  return (
    <div>
      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Person"
        message="Are you sure you want to delete this person? This action cannot be undone."
        confirmText="Delete"
        isDestructive
      />
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        accept="image/*" 
        onChange={(e) => handlePhotoUpload(e, uploadingId || undefined)} 
      />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{isDomainTab ? "Workspace Staff" : "External Contacts"}</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {isDomainTab
              ? "Review workspace people, departments, contact details, and synced profile records"
              : "Manage external contacts, company links, and communication details"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && isDomainTab && (
            <button 
              onClick={handleSyncWorkspace} 
              disabled={isSyncing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium disabled:opacity-50"
            >
              {isSyncing ? (
                <div className="w-4 h-4 border-2 border-slate-300 dark:border-slate-600 border-t-slate-700 dark:border-t-slate-200 rounded-full animate-spin" />
              ) : (
                <Users className="w-4 h-4" />
              )}
              Sync Workspace Staff
            </button>
          )}
          {!isDomainTab && (
            <button onClick={startCreatingPerson} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium">
              <Plus className="w-4 h-4" /> Add Person
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_380px]">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
                <tr className="h-10">
                  <th className="w-20 px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Photo</th>
                  <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Name</th>
                  <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Role</th>
                  {isDomainTab && <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Department</th>}
                  {!isDomainTab && <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Company</th>}
                  <th className="px-4 py-0 align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Contact</th>
                  <th className="px-4 py-0 text-right align-middle text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {isDomainTab ? (
                  Object.entries(groupedDomainUsers).map(([dept, deptPeople]) => (
                    <React.Fragment key={dept}>
                      <tr 
                        className="bg-slate-50/80 dark:bg-slate-950/80 hover:bg-slate-100 dark:hover:bg-slate-900 cursor-pointer transition-colors"
                        onClick={() => setCollapsedDepts(prev => ({ ...prev, [dept]: !prev[dept] }))}
                      >
                        <td colSpan={isDomainTab ? 6 : 7} className="px-4 py-2 font-medium text-slate-700 dark:text-slate-200">
                          <div className="flex items-center gap-2">
                            {collapsedDepts[dept] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {dept} <span className="text-slate-400 dark:text-slate-500 text-xs font-normal">({deptPeople.length})</span>
                          </div>
                        </td>
                      </tr>
                      {!collapsedDepts[dept] && deptPeople.map(renderPersonRow)}
                    </React.Fragment>
                  ))
                ) : (
                  displayPeople.map(renderPersonRow)
                )}
                {displayPeople.length === 0 && (
                  <tr><td colSpan={isDomainTab ? 6 : 7} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">No matching contacts found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 shadow-sm">
          {isAdding ? (
            <form onSubmit={handleSubmit} className="space-y-0">
              <div className="flex h-10 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-0">
                <div>
                  <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {editingId ? "Edit Person" : "Add Person"}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={stopEditingPerson}
                  className="rounded-lg p-2 text-slate-400 dark:text-slate-500 hover:bg-white dark:hover:bg-slate-900 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-4">

              {isDomainTab && (
                <div className="rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/20 p-3 text-sm text-blue-700 dark:text-blue-300">
                  <strong>Note:</strong> Name, Email, Role, Department, and Photo are synced from Google Workspace. Any changes made here may be overwritten during the next sync.
                </div>
              )}

              <div className="flex items-center gap-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="relative w-20 h-20 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                  <User className="w-8 h-8 text-slate-300 dark:text-slate-500" />
                  {formData.photoUrl ? (
                    <img
                      src={formData.photoUrl}
                      alt="Photo"
                      className="absolute inset-0 h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Person Photo</label>
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={() => { setUploadingId(null); fileInputRef.current?.click(); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium"
                    >
                      <Upload className="w-3 h-3" /> Upload
                    </button>
                    <button 
                      type="button" 
                      onClick={() => handleSearchPhoto(formData.name || "")}
                      disabled={!formData.name || searchingId === "new"}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium disabled:opacity-50"
                    >
                      {searchingId === "new" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                      Search Photo
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Name</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Company</label>
                  <select value={formData.companyId} onChange={e => setFormData({...formData, companyId: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100">
                    <option value="">Select Company...</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Role/Title</label>
                  <input type="text" value={formData.roleTitle || formData.role || ""} onChange={e => setFormData({...formData, roleTitle: e.target.value, role: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Department</label>
                  <input type="text" value={formData.department || ""} onChange={e => setFormData({...formData, department: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Email</label>
                  <input
                    type="email"
                    value={formData.email || ""}
                    onChange={e => {
                      const nextEmail = e.target.value;
                      setFormData(prev => {
                        const nextCompanyId = prev.companyId || findCompanyIdByEmail(nextEmail);
                        return { ...prev, email: nextEmail, companyId: nextCompanyId };
                      });
                    }}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Phone</label>
                  <input type="tel" value={formData.phone || ""} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-0.5 uppercase tracking-wider">Notes</label>
                  <textarea value={formData.notes || ""} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-100" rows={4} />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={stopEditingPerson} className="px-3 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-xs font-medium">Cancel</button>
                <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium">Save</button>
              </div>
              </div>
            </form>
          ) : selectedPerson ? (
            <div>
              <div className="flex h-10 items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-4 py-0">
                <div>
                  <h4 className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Person Details</h4>
                </div>
              </div>
              <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{selectedPerson.name}</h4>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{selectedPerson.roleTitle || selectedPerson.role || selectedPerson.title || "-"}</p>
                </div>
                {(!isDomainTab || isAdmin) && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => startEditingPerson(selectedPerson)} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Edit</button>
                    <button onClick={() => setDeleteId(selectedPerson.id)} className="rounded-lg border border-red-200 dark:border-red-900/40 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30">Delete</button>
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-center gap-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="relative w-20 h-20 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 flex items-center justify-center shrink-0">
                  <User className="w-8 h-8 text-slate-300 dark:text-slate-500" />
                  {selectedPerson.photoUrl ? (
                    <img
                      src={selectedPerson.photoUrl}
                      alt={selectedPerson.name}
                      className="absolute inset-0 h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-slate-900 dark:text-slate-100">{selectedPerson.name}</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{getPersonCompanyName(selectedPerson)}</p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Role</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{selectedPerson.roleTitle || selectedPerson.role || selectedPerson.title || "-"}</p>
                </div>
                {isDomainTab && (
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Department</p>
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{selectedPerson.department || "-"}</p>
                  </div>
                )}
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Company</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{getPersonCompanyName(selectedPerson)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Contact</p>
                  <div className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-200">
                    <p>{selectedPerson.email || "-"}</p>
                    <p>{selectedPerson.phone || "-"}</p>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Notes</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                    {selectedPerson.notes || "No notes yet."}
                  </p>
                </div>
              </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-6 text-center">
              <User className="h-10 w-10 text-slate-300 dark:text-slate-600" />
              <h4 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">Select a person</h4>
              <p className="mt-2 max-w-xs text-sm text-slate-500 dark:text-slate-400">
                Choose someone from the list to see their details here, or add a new contact.
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
